import React, { HTMLAttributes } from "react";
import _ from "lodash";
/// Message Modules
import pubsub from "core/global/global";
import CustomEvent from "core/customEvent/index";
import ShowDICOM from "core/DICOMtag";
//import PropTypes from 'prop-types';
import "./OrientationMarkers.styl";
import math from "core/math";
import { Vector3 } from "three/src/math/Vector3";
/**
 * @function getOrientationString
 * @description DICOM 태그에서 얻은 방향 벡터를 이용해 뷰어에 표시할 방향 문자를 획득
 * @param vector DICOM 태그에서 얻은 방향 벡터
 * @see {@link }
 * @todo link에 ohif 또는 관련 참고 문헌 추가필요
 * @return 방향 문자
 */
function getOrientationString(vector: any) {
  const vec3 = math.vector.convertToVector3(vector);

  let orientation = "";
  const orientationX = vec3.x < 0 ? "R" : "L";
  const orientationY = vec3.y < 0 ? "A" : "P";
  const orientationZ = vec3.z < 0 ? "F" : "H";

  // Should probably make this a function vector3.abs
  const abs = new Vector3(Math.abs(vec3.x), Math.abs(vec3.y), Math.abs(vec3.z));

  for (let i = 0; i < 3; i++) {
    if (abs.x > 0.0001 && abs.x > abs.y && abs.x > abs.z) {
      orientation += orientationX;
      abs.x = 0;
    } else if (abs.y > 0.0001 && abs.y > abs.x && abs.y > abs.z) {
      orientation += orientationY;
      abs.y = 0;
    } else if (abs.z > 0.0001 && abs.z > abs.x && abs.z > abs.y) {
      orientation += orientationZ;
      abs.z = 0;
    } else {
      break;
    }
  }

  return orientation;
}

/**
 * @function invertOrientationString
 * @description DICOM 영상에서 표시되는 방향 문자를 입력받아 반대 방향 문자로 변경하여 리턴
 * @see {@link }
 * @todo link에 ohif 또는 관련 참고 문헌 추가필요
 * @return 변환된 방향 문자
 */
function invertOrientationString(string: any) {
  let inverted = string.replace("H", "f");

  inverted = inverted.replace("F", "h");
  inverted = inverted.replace("R", "l");
  inverted = inverted.replace("L", "r");
  inverted = inverted.replace("A", "p");
  inverted = inverted.replace("P", "a");
  inverted = inverted.toUpperCase();

  return inverted;
}

const orientation = {
  getOrientationString,
  invertOrientationString
};

/**
 * Computes the orientation labels on a Viewport element
 * when the viewport settings change (e.g. when a horizontal flip or a rotation occurs)
 *
 * @param imageId The image data
 * @param viewport The current viewport
 */

/**
 * @function getOrientationString
 * @description 뷰포트 설정에 따른 방향 문자 획득(좌/우 위/아래 Flip 반영)
 * @param imageId 이미지 데이터
 * @param viewport 뷰포트 정보
 * @see {@link }
 * @todo link에 ohif 또는 관련 참고 문헌 추가필요
 * @return 방향 문자
 */
export function getOrientationMarkers(
  rowCosines: any,
  columnCosines: any,
  viewport: any
) {
  if (!rowCosines || !columnCosines) {
    return;
  }

  const rowString = orientation.getOrientationString(rowCosines);
  const columnString = orientation.getOrientationString(columnCosines);
  const oppositeRowString = orientation.invertOrientationString(rowString);
  const oppositeColumnString = orientation.invertOrientationString(
    columnString
  );

  const markers = {
    top: oppositeColumnString,
    left: oppositeRowString,
    bottom: columnString,
    right: rowString
  };

  // If any vertical or horizontal flips are applied, change the orientation strings ahead of
  // the rotation applications
  if (viewport.vflip) {
    markers.bottom = markers.top;
    markers.top = orientation.invertOrientationString(markers.top);
  }

  if (viewport.hflip) {
    markers.right = markers.left;
    markers.left = orientation.invertOrientationString(markers.left);
  }

  // Swap the labels accordingly if the viewport has been rotated
  // This could be done in a more complex way for intermediate rotation values (e.g. 45 degrees)
  if (viewport.rotation === 90 || viewport.rotation === -270) {
    return {
      top: markers.left,
      left: orientation.invertOrientationString(markers.top),
      bottom: orientation.invertOrientationString(markers.left),
      right: markers.top
    };
  } else if (viewport.rotation === -90 || viewport.rotation === 270) {
    return {
      top: orientation.invertOrientationString(markers.left),
      left: markers.top,
      bottom: markers.left,
      right: orientation.invertOrientationString(markers.top)
    };
  } else if (viewport.rotation === 180 || viewport.rotation === -180) {
    return {
      top: orientation.invertOrientationString(markers.top),
      left: orientation.invertOrientationString(markers.left),
      bottom: markers.top,
      right: markers.right
    };
  }

  return markers;
}

/**
 * @description 프로퍼티 타입 정의
 *
 * @interface IOrientationMarkerProps
 * @extends {HTMLAttributes<HTMLDivElement>}
 */
interface IOrientationMarkerProps extends HTMLAttributes<HTMLDivElement> {
  imageId: any;
  viewport: any;
  rowCosines: any;
  columnCosines: any;
  styles: any;
}

/**
 * @function ViewportOrientationMarkers
 * @description DICOM 영상에 방향 문자를 오버레이하는 UI 컴포넌트
 * @param {*} { imageId = null, viewport = null }
 * @returns 렌더링된 React Element
 */
// const ViewportOrientationMarkers: React.FunctionComponent<{
//   IOrientationMarkerProps: IOrientationMarkerProps;
// }> = props => {

class ViewportOrientationMarkers extends React.PureComponent<
  IOrientationMarkerProps
> {
  tokenToggleDicomTag = null;
  componentDidMount() {
    this.tokenToggleDicomTag = (pubsub as any).subscribe(
      CustomEvent.Messages.TOGGLE_DICOM_TAG,
      this.handleToggleDicomTag.bind(this)
    );
  }

  componentWillUnmount() {
    (pubsub as any).unsubscribe(this.tokenToggleDicomTag);
  }

  handleToggleDicomTag() {
    this.forceUpdate();
  }

  renderOrientationMarker = () => {
    if (!ShowDICOM.getIsShowDICOMTag()) {
      return;
    }

    if (!_.get(this.props, "imageId") || !_.get(this.props, "viewport")) {
      return "";
    }
    //const markers = getOrientationMarkers(props.IOrientationMarkerProps.imageId, props.IOrientationMarkerProps.viewport);
    const markers = getOrientationMarkers(
      _.get(this.props, "rowCosines"),
      _.get(this.props, "columnCosines"),
      _.get(this.props, "viewport")
    );
    if (!markers) {
      return "";
    }
    const styles = _.get(this.props, "styles");
    return (
      <React.Fragment>
        <div className="topMid orientationMarker dicomTag" style={styles}>
          {markers.top}
        </div>
        <div className="leftMid orientationMarker dicomTag" style={styles}>
          {markers.left}
        </div>
        <div className="bottomMid orientationMarker dicomTag" style={styles}>
          {markers.bottom}
        </div>
        <div className="rightMid orientationMarker dicomTag" style={styles}>
          {markers.right}
        </div>
      </React.Fragment>
    );
  };

  render() {
    return (
      <div className="viewportOrientationMarkers noselect nodrag">
        {this.renderOrientationMarker()}
      </div>
    );
  }
}

export default ViewportOrientationMarkers;
